import "./statistics.css";
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  CustomTooltipProps,
  LineChartProps,
  OperationStatisticsProps,
  SeriesPointProps,
} from "./statistics.props";
import { vars } from "../../../../assets";

/* ---------- CustomTooltip ---------- */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  lines,
  detailed = false,
  showDot = false,
  currencySymbol = "€",
}) => {
  if (!active || !payload?.length) return null;

  // payload contains an entry per Line rendered
  return (
    <div
      className="statistics-custom-tooltip-container"
      style={{
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: 18,
        minWidth: 220,
        border: "1px solid #e6e6e6",
      }}
    >
      <div style={{ color: "#8b8f95", fontSize: 12, marginBottom: 6 }}>
        {label}
      </div>

      {/* For each series in payload show its total + breakdown */}
      {payload.map((p, i) => {
        // p.dataKey might be "sales" or "seriesA.total" depending on how you passed dataKey.
        // We assume we passed base key to lines and used `${key}.total` as the Line dataKey.
        const fullKey: string = p.dataKey;
        const baseKey = fullKey.split(".")[0]; // "sales"
        const rowPoint: SeriesPointProps | undefined = p.payload?.[baseKey];

        // fallback if payload provided direct numeric value
        const totalValue = rowPoint ? rowPoint.total : p.value;

        const lineMeta = lines.find((l) => l.dataKey === baseKey);

        return (
          <div key={i} style={{ marginBottom: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  gap: 8,
                }}
              >
                {showDot && (
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 99,
                      background: p.color || lineMeta?.color,
                    }}
                  />
                )}
                <div className="statistics-tooltip-amount">
                  {Number(totalValue).toLocaleString("es-ES")}
                  {currencySymbol}
                </div>
                <div className="statistics-tooltip-description">
                  {lineMeta?.name || baseKey}
                </div>
              </div>
            </div>

            {/* breakdown list */}
            {detailed &&
              rowPoint !== undefined &&
              rowPoint?.breakdown !== undefined &&
              rowPoint?.breakdown?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {rowPoint.breakdown.map((b, bi) => (
                    <div
                      key={bi}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {/* icon: if URL show image, else show emoji/text */}
                        {b.icon &&
                          (b.icon.startsWith("http") ||
                          b.icon.startsWith("/") ? (
                            <img
                              src={b.icon}
                              alt={b.name}
                              style={{
                                width: 20,
                                height: 20,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: 20,
                                height: 20,
                                fontSize: 10,
                                fontWeight: "bold",
                                color: b.color ?? vars.colorBlackLight,
                              }}
                            >
                              {b.icon}
                            </span>
                          ))}
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: "bold",
                            color: b.color ?? vars.colorBlackLight,
                          }}
                        >
                          {b.name}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: "bold",
                          color: b.color ?? vars.colorBlackLight,
                        }}
                      >
                        {b.value.toLocaleString("es-ES")}
                        {currencySymbol}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
};

/* ---------- LineChartComponent (reusable) ---------- */
const LineChartComponent: React.FC<LineChartProps> = ({
  data,
  lines,
  detailedTooltip = false,
  showDot = false,
  currencySymbol = "€",
  height = 320,
}) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={data}
        margin={{ top: 20, right: 0, left: 10, bottom: 0 }}
      >
        <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
        <XAxis dataKey="date" tickFormatter={(tick) => tick.slice(0, 2)} />
        <YAxis
          orientation="right"
          tickFormatter={(v: any) => `${Number(v / 1000).toFixed(0)}K`}
        />

        <Tooltip
          content={({ active, payload, label }) => (
            <CustomTooltip
              active={!!active}
              payload={payload}
              label={`${label ?? ""}`}
              lines={lines}
              showDot={showDot}
              detailed={detailedTooltip}
              currencySymbol={currencySymbol}
            />
          )}
          cursor={{ stroke: "#ccc", strokeDasharray: "4 4" }}
        />

        {lines.map((line, idx) => (
          // NOTE: we render the numeric value using nested path `${dataKey}.total`
          <Line
            key={line.dataKey}
            type="linear"
            dataKey={`${line.dataKey}.total`}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

/* ---------- Main exported component ---------- */
const OperationStatistics: React.FC<OperationStatisticsProps> = ({
  data,
  lines,
  detailedTooltip = false,
  showDot = false,
  currencySymbol = "€",
  title,
  height,
}) => {
  return (
    <div>
      {title && <h3 style={{ marginBottom: 8 }}>{title}</h3>}
      <LineChartComponent
        data={data}
        lines={lines}
        detailedTooltip={detailedTooltip}
        showDot={showDot}
        currencySymbol={currencySymbol}
        height={height}
      />
    </div>
  );
};

export default OperationStatistics;
